import { z } from "zod";
//#region src/app-rollout-diagnostics.ts
/**
* App rollout-diagnostics wire types — GET /v1/apps/:appId/rollout-diagnostics
* (release-engine apps module; #542). Promotes the transient "why isn't this
* app rolling" reasons — today only WARN-logged inside the desired-state
* builder (operator-api.service.ts:940-945 / :1007-1017) — into a structured,
* resource-scoped read the `zion-app status` CLI merges client-side.
*
* `:appId` is the `release.apps.id` UUID (a PATH param, resource-scoped by
* `@RequireResourcePermission('cp:app:read','appId')`); it is echoed as
* `appId`. The blocks are a PURE RECOMPUTE over the app's latest composition —
* identical inputs to `computeDesiredState`, computed via the SAME extracted
* predicates the builder uses, so the read can never drift from what the
* operator actually deploys.
*/
/**
* Why a fixture is excluded from desired state. Closed union — each value maps
* to exactly one extracted predicate:
*  - `no-image-ref`            → the pinned `app_versions` row has a null/absent
*                                `image_ref` (pre-G13 row or missing publish).
*  - `pending-required-config` → a `config.schema` key is `required`, has no
*                                `default`, no `app_fixture_configs` value, and
*                                is neither a reserved operator-injected env
*                                name nor a declared `platformSecrets` key.
*  - `foreign-image`           → the row's `image_ref` names a DIFFERENT
*                                registered app's repository (#2264). Deploying
*                                it would run another tenant's container under
*                                this app's identity, so the fixture is blocked
*                                rather than rolled.
*/
const rolloutBlockKindSchema = z.enum([
	"no-image-ref",
	"pending-required-config",
	"foreign-image"
]);
/** One blocking reason for one fixture. */
const rolloutBlockSchema = z.object({
	/** The fixture id (`resolvedFixtures` key / `app_versions.fixture_name`). */
	fixtureId: z.string(),
	/** The pinned version the block was computed against — always the pinned
	*  `resolvedFixtures` version, even for `no-image-ref` (where no
	*  `app_versions` row exists). Kept `.nullable()` as a harmless superset;
	*  the producer never emits null today. */
	version: z.string().nullable(),
	/** The block classification. */
	kind: rolloutBlockKindSchema,
	/** Human sentence — the SAME message text the builder WARN-logs, so ops
	*  grep and CLI output stay identical. */
	detail: z.string(),
	/** Present only for `pending-required-config`: the unsatisfied schema key. */
	configKey: z.string().optional()
}).strict();
/**
* Cap on `blocks`. Generous: a block is one (fixture × reason) on the app's
* latest composition, and an app has tens of fixtures. 500 bounds the body
* without being reachable by any real app.
*/
const ROLLOUT_BLOCKS_MAX = 500;
/** Diagnostics for one app. `blocked === blocks.length > 0`. */
const appRolloutDiagnosticsSchema = z.object({
	/** Echoes the requested app UUID (`release.apps.id`) verbatim. */
	appId: z.string().uuid(),
	/** True iff at least one fixture is blocked. */
	blocked: z.boolean(),
	/** One entry per blocked fixture; empty when nothing blocks the rollout. */
	blocks: z.array(rolloutBlockSchema),
	/**
	* ⛔ **`blocks` hit the server cap** (`ROLLOUT_BLOCKS_MAX`, spine V-1).
	*
	* This read is EXEMPT from the V-1 cursor because it is bounded by the DP
	* fleet, which is operator-scale — but "bounded by something small" is a
	* claim about today's fleet, and a claim is not a guarantee. The flag is the
	* guarantee: `false` means a caller may rely on having every block.
	*
	* ⚠️ `blocked` is UNAFFECTED by truncation — it is still true iff any
	* fixture is blocked, because the cap is applied after the recompute. A
	* `truncated: true` with `blocked: true` means "at least these"; it never
	* means "possibly none".
	*/
	truncated: z.boolean(),
	/** ISO-8601 recompute timestamp. */
	computedAt: z.string().datetime()
}).strict();
//#endregion
export { ROLLOUT_BLOCKS_MAX, appRolloutDiagnosticsSchema, rolloutBlockKindSchema, rolloutBlockSchema };

//# sourceMappingURL=app-rollout-diagnostics.js.map